(function() {
  'use strict';

  const ShoppableArticle = {
    init: function() {
      this.bindEvents();
      this.hydrateInlineMarkers();
      this.wireInlineProducts();
      this.autoInsertInlineProducts();
    },

    bindEvents: function() {
      // The section re-runs this script on every theme-editor section render; a second
      // listener would add to cart twice per click.
      if (this._eventsBound) return;
      this._eventsBound = true;

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.sa-atc-btn, .pc-atc[data-variant-id]');
        if (btn) {
          e.preventDefault();
          this.handleAddToCart(btn);
        }
      });
    },

    getProducts: function() {
      if (this._products) return this._products;
      const el = document.querySelector('.sa-products-data');
      if (!el) {
        this._products = [];
        return this._products;
      }
      try {
        this._products = JSON.parse(el.textContent) || [];
      } catch (error) {
        console.error('Shoppable article: invalid product data', error);
        this._products = [];
      }
      return this._products;
    },

    // Editors type [[PRODUCT]], [[PRODUCT 2]] or [[PRODUCT handle]] in the blog post body.
    // snippets/inline-product-markers.liquid turns each token into an empty
    // <span class="sa-inline-marker" data-product-ref="..."> server-side; this resolves
    // that reference against the serialized recommended products and places the card.
    hydrateInlineMarkers: function() {
      const articleBody = document.querySelector('.sa-article-body');
      if (!articleBody) return;

      const markers = Array.from(articleBody.querySelectorAll('.sa-inline-marker[data-product-ref]'))
        .filter((marker) => !marker.closest('.sa-shop-grid'));
      if (!markers.length) return;

      // Any marker means the editor chose the placement, so the heuristic stands down even
      // if every reference fails to resolve. Inferring this from rendered cards instead
      // would silently fall back to guessed positions on exactly the broken articles.
      this._editorPlacedMarkers = true;

      const products = this.getProducts().filter((product) => product && product.variant_id);
      const used = new Set();

      // Legacy div markers claim their product first so a bare token can't repeat it.
      document.querySelectorAll('.sa-inline-product[data-product-index]').forEach((el) => {
        const index = parseInt(el.getAttribute('data-product-index'), 10);
        if (!isNaN(index)) used.add(index);
      });

      const entries = markers.map((marker) => ({
        marker: marker,
        ref: this.normalizeProductRef(marker.getAttribute('data-product-ref'))
      }));

      // Named and numbered references resolve first, so [[PRODUCT]] never steals one.
      entries.forEach((entry) => {
        if (!entry.ref) return;
        entry.index = this.resolveProductRef(entry.ref, products);
        if (typeof entry.index === 'number') used.add(entry.index);
      });

      entries.forEach((entry) => {
        if (entry.ref) return;
        const next = products.findIndex((product, index) => !used.has(index));
        entry.index = next === -1 ? undefined : next;
        if (next !== -1) used.add(next);
      });

      entries.forEach((entry) => {
        const product = typeof entry.index === 'number' ? products[entry.index] : null;

        if (!product) {
          this.reportUnresolvedMarker(entry.marker, entry.ref, products.length);
          entry.marker.remove();
          return;
        }

        // Anchor on the outermost ancestor still inside the body, so a token written
        // inside a list or blockquote places the card after that block rather than in it.
        let anchor = entry.marker;
        while (anchor.parentNode && anchor.parentNode !== articleBody) {
          anchor = anchor.parentNode;
        }

        const card = document.createElement('div');
        card.className = 'product-card';
        card.setAttribute('data-inline-product-source', 'marker');
        this.renderInlineCard(card, product);
        // insertBefore rather than insertAdjacentElement: the anchor is the marker span
        // itself when the token sits directly in the body, and that may be a non-element.
        anchor.parentNode.insertBefore(card, anchor.nextSibling);
        entry.marker.remove();
      });
    },

    normalizeProductRef: function(raw) {
      return (raw || '')
        .replace(/[​-‏⁠﻿]/g, '')
        .replace(/[‐-―−]/g, '-')
        .replace(/[‘’“”]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    },

    resolveProductRef: function(ref, products) {
      if (/^\d+$/.test(ref)) {
        const position = parseInt(ref, 10);
        return position >= 1 && position <= products.length ? position - 1 : undefined;
      }

      const handle = ref
        .replace(/^https?:\/\/[^/]+/i, '')
        .replace(/[?#].*$/, '')
        .replace(/^.*\/products\//i, '')
        .replace(/\/+$/, '')
        .toLowerCase();

      const index = products.findIndex((product) => product.handle === handle);
      return index === -1 ? undefined : index;
    },

    reportUnresolvedMarker: function(marker, ref, productCount) {
      const label = ref ? '[[PRODUCT ' + ref + ']]' : '[[PRODUCT]]';
      const reason = productCount
        ? 'no matching recommended product'
        : 'this article has no recommended products set';

      console.warn('Shoppable article: ' + label + ' was not placed - ' + reason + '.');

      // Shoppers never see the token, so surface the failure to whoever can fix it.
      if (!this.isMarkerDebug() || !marker.parentNode) return;

      const notice = document.createElement('p');
      notice.className = 'sa-marker-notice';
      notice.textContent = label + ' could not be placed: ' + reason + '.';
      marker.parentNode.insertBefore(notice, marker);
    },

    isMarkerDebug: function() {
      return Boolean(window.Shopify && window.Shopify.designMode) ||
        /[?&]sa_debug=1/.test(window.location.search);
    },

    // Author places empty <div class="sa-inline-product" data-product-handle="..."></div>
    // (or data-product-index="0") markers in the article body; we render them from the
    // same custom.blog_recommended_products metafield used by the rail and grid. Existing
    // hand-authored .sa-inline-product[data-variant-id] cards keep their content and get
    // a legacy add-to-cart button appended.
    wireInlineProducts: function() {
      const products = this.getProducts();

      document.querySelectorAll('.sa-inline-product').forEach((card) => {
        if (card.querySelector('.pc-atc, .sa-inline-product__atc, .sa-atc-btn')) return;

        let product = null;
        const handle = card.getAttribute('data-product-handle');
        const index = card.getAttribute('data-product-index');
        const variantId = card.getAttribute('data-variant-id');
        const hasProductMarker = Boolean(handle) || index !== null;

        if (handle) {
          product = products.find((p) => p.handle === handle);
        } else if (index !== null) {
          product = products[parseInt(index, 10)];
        }

        if (product) {
          this.renderInlineCard(card, product);
          return;
        }

        if (variantId) {
          this.appendLegacyInlineButton(card, variantId);
          return;
        }

        if (hasProductMarker && this.isEmptyInlineMarker(card)) {
          card.style.display = 'none';
        }
      });
    },

    autoInsertInlineProducts: function() {
      const articleBody = document.querySelector('.sa-article-body');
      if (!articleBody) return;

      if (this._editorPlacedMarkers) return;

      const section = document.querySelector('.shoppable-article-section');
      if (section && section.getAttribute('data-auto-inline') === 'false') return;

      const hasInlineCards = Array.from(articleBody.querySelectorAll('.sa-inline-product, .product-card'))
        .some((card) => !card.closest('.sa-shop-grid'));
      if (hasInlineCards) return;

      const products = this.getProducts().filter((product) => product && product.variant_id);
      const cardCount = Math.min(2, products.length);
      if (!cardCount) return;

      const insertionPoints = this.getInlineInsertionPoints(articleBody, cardCount);
      insertionPoints.forEach((point, index) => {
        const card = document.createElement('div');
        card.className = 'product-card';
        card.setAttribute('data-auto-inline-product', 'true');
        this.renderInlineCard(card, products[index]);
        point.insertAdjacentElement('afterend', card);
      });
    },

    getInlineInsertionPoints: function(articleBody, count) {
      const contentChildren = [];

      Array.from(articleBody.children).some((child) => {
        if (child.classList.contains('sa-shop-grid')) return true;
        contentChildren.push(child);
        return false;
      });

      const strongParagraphs = contentChildren.filter((child) => this.isMeaningfulParagraph(child, 70));
      const fallbackParagraphs = contentChildren.filter((child) => this.isMeaningfulParagraph(child, 25));
      const paragraphs = strongParagraphs.length >= count ? strongParagraphs : fallbackParagraphs;
      const desiredCount = Math.min(count, paragraphs.length);

      if (!desiredCount) return [];
      if (desiredCount === 1) {
        return [paragraphs[this.getNearestUnusedIndex(paragraphs, 0.45, [])]];
      }

      const usedIndexes = [];
      [0.35, 0.65].slice(0, desiredCount).forEach((fraction) => {
        usedIndexes.push(this.getNearestUnusedIndex(paragraphs, fraction, usedIndexes));
      });

      return usedIndexes
        .sort((a, b) => a - b)
        .map((index) => paragraphs[index]);
    },

    getNearestUnusedIndex: function(items, fraction, usedIndexes) {
      const maxIndex = items.length - 1;
      let targetIndex = Math.round(maxIndex * fraction);

      if (items.length > 2 && targetIndex === 0) targetIndex = 1;
      if (items.length > 3 && targetIndex === maxIndex) targetIndex = maxIndex - 1;

      for (let offset = 0; offset <= maxIndex; offset += 1) {
        const forward = targetIndex + offset;
        if (forward <= maxIndex && !usedIndexes.includes(forward)) return forward;

        const backward = targetIndex - offset;
        if (backward >= 0 && !usedIndexes.includes(backward)) return backward;
      }

      return targetIndex;
    },

    isMeaningfulParagraph: function(element, minLength) {
      if (!element || element.tagName !== 'P') return false;
      if (element.closest('.sa-shop-grid')) return false;

      const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
      if (text.length < minLength) return false;

      const hasMedia = Boolean(element.querySelector('img, picture, svg, iframe, video'));
      return !hasMedia || text.length >= 120;
    },

    isEmptyInlineMarker: function(card) {
      return card.textContent.trim() === '' && card.children.length === 0;
    },

    appendLegacyInlineButton: function(card, variantId) {
      const productName = card.querySelector('.sa-inline-product__name, .pc-name')?.textContent?.trim() || 'Product';
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'sa-inline-product__atc sa-atc-btn';
      btn.setAttribute('data-variant-id', variantId);
      btn.setAttribute('data-product-name', productName);
      btn.setAttribute('aria-label', `Add ${productName} to cart`);
      btn.textContent = 'Add to Cart';
      card.appendChild(btn);
    },

    renderInlineCard: function(card, product) {
      card.innerHTML = '';
      card.classList.remove('sa-inline-product');
      card.classList.add('product-card');

      // Announce the card as one promotional unit; it interrupts the reading flow.
      card.setAttribute('role', 'group');
      card.setAttribute('aria-label', `Recommended product: ${product.title}`);

      // Contract read by getCardItem() in ga4-events.js. Without these, editor-placed
      // cards generate no view_item_list or select_item at all.
      card.setAttribute('data-ga4-item', '');
      card.setAttribute('data-ga4-item-id', product.sku || product.variant_id || '');
      card.setAttribute('data-ga4-variant-id', product.variant_id || '');
      card.setAttribute('data-ga4-item-name', product.title || '');
      card.setAttribute('data-ga4-item-brand', product.vendor || '');
      card.setAttribute('data-ga4-item-category', product.type || 'Blog Inline Product');
      card.setAttribute('data-ga4-item-variant', product.variant_title || '');
      card.setAttribute('data-ga4-price', product.price_amount == null ? '' : product.price_amount);
      card.setAttribute('data-ga4-list-name', 'Blog Inline Products');

      const imageWrap = document.createElement('div');
      imageWrap.className = 'pc-img';
      if (product.image) {
        const img = document.createElement('img');
        img.src = product.image;
        img.alt = ''; // Decorative: the title follows immediately.
        img.loading = 'lazy';
        img.width = 80;
        img.height = 80;
        imageWrap.appendChild(img);
      }

      const info = document.createElement('div');
      info.className = 'pc-info';

      const bestFor = document.createElement('p');
      if (product.best_for) {
        bestFor.className = 'pc-best-for';
        bestFor.textContent = product.best_for;
      } else {
        // Same box, unpainted — keeps cards level when a product has no label set.
        bestFor.className = 'pc-best-for pc-best-for--placeholder';
        bestFor.setAttribute('aria-hidden', 'true');
        bestFor.textContent = ' ';
      }
      info.appendChild(bestFor);

      const name = document.createElement('p');
      name.className = 'pc-name';
      if (product.url) {
        // Also what makes GA4's expert_advice_article_product_click fire for these cards.
        const nameLink = document.createElement('a');
        nameLink.href = product.url;
        nameLink.textContent = product.title;
        name.appendChild(nameLink);
      } else {
        name.textContent = product.title;
      }
      info.appendChild(name);

      const price = document.createElement('p');
      price.className = 'pc-price';
      price.appendChild(document.createTextNode(product.price));
      if (product.subtitle) {
        price.appendChild(document.createTextNode(' '));
        const count = document.createElement('span');
        count.className = 'pc-count';
        count.textContent = `· ${product.subtitle}`;
        price.appendChild(count);
      }
      info.appendChild(price);

      const ratingCount = Number(product.rating_count) || 0;
      if (ratingCount > 0) {
        const stars = document.createElement('p');
        stars.className = 'pc-stars';
        const filled = Math.max(0, Math.min(5, Math.round(product.rating || 0)));
        const starSpan = document.createElement('span');
        starSpan.textContent = '★★★★★'.slice(0, filled) + '☆☆☆☆☆'.slice(0, 5 - filled);
        stars.appendChild(starSpan);
        stars.appendChild(
          document.createTextNode(' \u00a0' + ratingCount.toLocaleString() + ' reviews')
        );
        info.appendChild(stars);
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pc-atc';
      btn.setAttribute('data-variant-id', product.variant_id);
      btn.setAttribute('data-product-name', product.title);
      // Explicit false only, so JSON predating the field still offers the add.
      if (product.available === false) {
        btn.disabled = true;
        btn.setAttribute('aria-label', `${product.title} is sold out`);
        btn.textContent = 'Sold out';
      } else {
        // Keep the visible label as a prefix so voice control ("click Add to Cart") matches.
        btn.setAttribute('aria-label', `Add to Cart: ${product.title}`);
        btn.textContent = 'Add to Cart';
      }

      card.appendChild(imageWrap);
      card.appendChild(info);
      card.appendChild(btn);
    },

    getCartAddJsonUrl: function() {
      const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      const queryIndex = cartAddUrl.indexOf('?');
      const path = queryIndex === -1 ? cartAddUrl : cartAddUrl.slice(0, queryIndex);
      const query = queryIndex === -1 ? '' : cartAddUrl.slice(queryIndex);

      if (/\.js\/?$/.test(path)) {
        return path.replace(/\/$/, '') + query;
      }

      if (/\/cart\/add\/?$/.test(path)) {
        return path.replace(/\/?$/, '.js') + query;
      }

      return cartAddUrl;
    },

    handleAddToCart: function(button) {
      const variantId = button.getAttribute('data-variant-id');
      if (!variantId) return;

      button.setAttribute('aria-busy', 'true');
      const originalText = button.textContent;

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender()
        : [{ id: 'cart-icon-bubble' }];

      if (sections.length) {
        formData.append('sections', sections.map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
      }

      if (cart && typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(button);
      }

      fetch(this.getCartAddJsonUrl(), {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      })
        .then((response) => {
          const contentType = (response.headers.get('content-type') || '').toLowerCase();
          if (!contentType.includes('json') && !contentType.includes('javascript')) {
            throw new Error(`Expected JSON cart response, received ${contentType || 'unknown content type'}`);
          }
          return response.json();
        })
        .then((response) => {
          if (response.status) {
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'shoppable-article',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message
              });
            }

            // Shopify rejects sold-out variants with a 422. Without this the button
            // just resets and the shopper thinks the add silently worked.
            this.showToast(
              response.description || response.message || 'Sorry, this item could not be added to your cart.',
              true
            );
            return;
          }

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'shoppable-article',
              productVariantId: variantId,
              cartData: response
            });
          }

          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
          } else {
            this.renderSections(response, sections);
          }

          const productName = button.getAttribute('data-product-name') || 'Item';
          this.showToast(`${productName} added to cart ✓`);
        })
        .catch((error) => {
          console.error('Shoppable article ATC error:', error);
          this.showToast('Sorry, this item could not be added to your cart.', true);
        })
        .finally(() => {
          button.setAttribute('aria-busy', 'false');
        });
    },

    renderSections: function(response, sections) {
      if (!response.sections) return;

      sections.forEach((section) => {
        const target = document.getElementById(section.id);
        const sectionHtml = response.sections[section.id];
        if (!target || !sectionHtml) return;

        const selector = section.selector || '.shopify-section';
        const source = new DOMParser()
          .parseFromString(sectionHtml, 'text/html')
          .querySelector(selector);
        if (source) {
          target.innerHTML = source.innerHTML;
        }
      });
    },

    showToast: function(message, isError) {
      const toast = document.querySelector('.sa-toast');
      if (!toast) return;

      const msgEl = toast.querySelector('.sa-toast-message');
      if (msgEl) {
        msgEl.textContent = message;
      }

      toast.classList.toggle('sa-toast--error', Boolean(isError));
      toast.setAttribute('role', isError ? 'alert' : 'status');
      toast.classList.add('sa-show');

      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => {
        toast.classList.remove('sa-show');
      }, 2800);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      ShoppableArticle.init();
    });
  } else {
    ShoppableArticle.init();
  }

  // The theme editor swaps the section's markup without a page load, which drops the
  // hydrated cards; re-run against the fresh DOM. bindEvents() guards its own re-entry.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      if (!event.target || !event.target.querySelector('.shoppable-article-section')) return;
      ShoppableArticle._editorPlacedMarkers = false;
      ShoppableArticle.init();
    });
  }
})();
